/**
 * AGRI-OPERATIONAL-SECTOR-R2 — shared operational time/age formatting.
 *
 * Extracted so the Dashboard and the Sector Detail phrase "il y a X" and
 * "depuis X" identically. A divergence here would make the same telemetry look
 * like two different ages on two screens.
 *
 * Every function is i18n-driven (the caller passes `t`) — no hardcoded copy.
 */

import type { TFunction } from 'i18next';

/** Placeholder for a genuinely absent value. Never used to mean "zero". */
export const UNKNOWN_VALUE = '—';

/**
 * Human age of a timestamp ("à l'instant" / "il y a 23 s" / "il y a 18 min" /
 * "il y a 7 h"). A null timestamp yields the explicit "unavailable" copy — it
 * is never rendered as "0 s ago".
 */
export function relativeAge(ts: number | null, now: number, t: TFunction): string {
  if (ts === null) return t('agrisafeR1:dashboard.relative.unavailable');
  const seconds = Math.max(0, Math.floor((now - ts) / 1000));
  if (seconds < 10) return t('agrisafeR1:dashboard.relative.now');
  if (seconds < 60) return t('agrisafeR1:dashboard.relative.secondsAgo', { count: seconds });
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return t('agrisafeR1:dashboard.relative.minutesAgo', { count: minutes });
  return t('agrisafeR1:dashboard.relative.hoursAgo', { count: Math.floor(minutes / 60) });
}

/** Duration copy for a proven state ("18 min" / "2 h"). */
export function formatDuration(ms: number, t: TFunction): string {
  const minutes = Math.max(1, Math.floor(ms / 60000));
  if (minutes < 60) return t('agrisafeR1:dashboard.duration.minutes', { count: minutes });
  return t('agrisafeR1:dashboard.duration.hours', { count: Math.floor(minutes / 60) });
}

/**
 * The truthful "since" line for a sector.
 *
 *  - a PROVEN transition  → "depuis 18 min"
 *  - no proven transition but a known confirmation → "Dernière confirmation il y a X"
 *  - nothing provable     → "Durée inconnue"
 *
 * This never invents a duration from the current snapshot's age: a controller
 * that has been reporting OPEN for 3 minutes has not necessarily been open for
 * 3 minutes.
 */
export function sinceText(
  stateSinceMs: number | null,
  lastConfirmationMs: number | null,
  now: number,
  t: TFunction,
): string {
  if (stateSinceMs !== null) {
    return t('agrisafeR1:dashboard.since', { duration: formatDuration(stateSinceMs, t) });
  }
  if (lastConfirmationMs !== null) {
    return t('agrisafeR1:dashboard.lastConfirmation', { duration: relativeAge(lastConfirmationMs, now, t) });
  }
  return t('agrisafeR1:dashboard.durationUnknown');
}
